import { captureException } from '@sentry/nextjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-cool-inview'

export enum LoadingType {
  INFINITE,
  MANUAL,
}

export interface BaseTByCursor {
  id: string
}

export interface InfiniteLoadByCursorProps<T> {
  id: string
  getItems: (lastID: string) => Promise<{ items: T[]; count: number; nextID: string }>
  loadingType: LoadingType
  isEqual?: (a: T, b: T) => boolean
}

export interface InfiniteLoadByCursorReturn<T> {
  items: T[]
  count?: number
  hasNext: boolean
  loadNext: () => void
  loadMoreRef: any
  refresh: () => void
  newItem: (item: T) => void
  deleteItem: (id: string) => void
  setItems: (items: T[]) => void
  isLoading: boolean
}

export function useInfiniteLoadByCursor<T extends BaseTByCursor>(
  props: InfiniteLoadByCursorProps<T>
): InfiniteLoadByCursorReturn<T> {
  const { getItems, loadingType, isEqual } = props
  const [items, setItems] = useState<T[]>([])
  const [count, setCount] = useState<number | undefined>(undefined)
  const lastID = useRef('')
  const allPagesLoaded = useRef(false)
  const initialPageLoaded = useRef(false)
  const [hasNext, setHasNext] = useState(true)
  const isInFlight = useRef(false)
  const triggerNext = useRef(false)
  const remainingPagesToAutoload = useRef(loadingType === LoadingType.MANUAL ? 0 : -1)

  const [isLoading, setIsLoading] = useState(true)

  // refresh
  const refresh = (): void => {
    loadItems('', true)
  }

  // newItem
  const newItem = useCallback(
    (item: T): void => {
      const newItems = [item, ...items]
      setItems(newItems)
      setCount(newItems.length)
    },
    [items]
  )

  const deleteItem = useCallback(
    (id: string): void => {
      const newItems = items.filter((value) => value.id !== id)
      setItems(newItems)
      setCount(newItems.length)
    },
    [items]
  )

  const loadItems = useCallback(
    async (id: string, refresh: boolean): Promise<void> => {
      try {
        setIsLoading(true)
        const index = items.findIndex((value) => value.id === id)

        isInFlight.current = true
        const data = await getItems(
          refresh && index !== -1 ? (index === 0 ? '' : items[index - 1].id) : id
        )

        lastID.current = data.nextID
        isInFlight.current = false

        setItems((prevItems) => {
          const itemsToKeep = prevItems.slice(0, refresh && index !== -1 ? index : index + 1)
          return [
            ...itemsToKeep.filter((itemToKeep) =>
              isEqual ? !data.items.find((item) => isEqual(itemToKeep, item)) : true
            ),
            ...data.items,
          ]
        })
        setCount(data.count)

        if (data.items.length === 0) {
          setHasNext(false)
          allPagesLoaded.current = true
        } else {
          setHasNext(true)
          allPagesLoaded.current = false
        }
      } catch (err) {
        console.error(err)
        captureException(err)
      } finally {
        setIsLoading(false)
      }
    },
    [getItems, items]
  )

  const loadNext = (): void => {
    loadItems(lastID.current, false)
  }

  useEffect(() => {
    if (initialPageLoaded.current) {
      return
    }

    loadItems(lastID.current, false)
    initialPageLoaded.current = true
  }, [loadItems])

  useEffect(() => {
    refresh()
  }, [props.id])

  const { observe, unobserve } = useInView({
    rootMargin: '200px 0px',
    onEnter: () => {
      if (remainingPagesToAutoload.current === 0) {
        unobserve()
        return
      }

      remainingPagesToAutoload.current = remainingPagesToAutoload.current - 1
      if (isInFlight.current) {
        triggerNext.current = true
      } else {
        loadNext()
      }
    },
  })

  return {
    items,
    count,
    hasNext,
    loadNext,
    loadMoreRef: observe,
    refresh,
    newItem,
    deleteItem,
    setItems,
    isLoading,
  }
}
